import React from 'react'
import { Link } from 'react-router-dom'

const cx = 'max-w-[760px] mx-auto px-5 sm:px-8'

function H2({ children }) {
  return (
    <h2 className="font-display font-black text-[26px] sm:text-[30px] tracking-tight1 uppercase text-ds-text mt-12 mb-4">
      {children}
    </h2>
  )
}

function H3({ children }) {
  return (
    <h3 className="font-display font-extrabold text-[16px] tracking-[0.02em] uppercase text-ds-text mt-8 mb-3">
      {children}
    </h3>
  )
}

function P({ children }) {
  return <p className="font-sans text-[15px] text-ds-muted leading-[1.75] mb-4">{children}</p>
}

function Li({ children }) {
  return <li className="font-sans text-[15px] text-ds-muted leading-[1.75] mb-2">{children}</li>
}

export default function RefundPolicyPage() {
  return (
    <div className="min-h-screen bg-ds-bg">
      {/* Minimal top bar with back link */}
      <nav className="border-b border-ds-border h-14 flex items-center">
        <div className={`${cx} w-full flex items-center justify-between`}>
          <Link to="/" className="flex items-center gap-2.5 no-underline">
            <div className="w-7 h-7 bg-ds-red rounded-sharp flex items-center justify-center flex-shrink-0">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                <path
                  d="M1.5 3.2L6.5 1L11.5 3.2V6.5C11.5 9.5 6.5 12 6.5 12C6.5 12 1.5 9.5 1.5 6.5V3.2Z"
                  fill="white"
                  opacity="0.9"
                />
              </svg>
            </div>
            <span className="font-display font-black text-[14px] tracking-[0.04em] uppercase text-ds-text">
              GuionCreator
            </span>
          </Link>
          <Link
            to="/"
            className="font-display font-bold text-[12px] tracking-[0.10em] uppercase text-ds-muted no-underline hover:text-ds-text transition-colors"
          >
            ← Volver
          </Link>
        </div>
      </nav>

      <article className={`${cx} py-16 sm:py-24`}>
        <p className="font-display font-bold text-label tracking-label uppercase text-ds-red mb-4">
          Política de reembolso
        </p>
        <h1 className="font-display font-black text-[44px] sm:text-[64px] tracking-tight2 uppercase text-ds-text leading-[0.95] mb-8">
          Queremos que
          <br />
          lo pruebes
          <br />
          <span className="italic text-ds-red">sin miedo.</span>
        </h1>

        <P>
          A la vez, los créditos son un producto consumible y tenemos que protegernos contra abusos.
          Esto es lo que aplicamos, dicho en claro y sin letra pequeña.
        </P>
        <p className="font-display font-bold text-label tracking-caption uppercase text-ds-muted mt-6">
          Última actualización: 21 de abril de 2026
        </p>

        <H2>Cancelación</H2>
        <P>
          Puedes cancelar tu suscripción en cualquier momento con{' '}
          <strong className="text-ds-text">1 click</strong> desde el portal de Stripe vinculado a tu
          cuenta. No hay llamadas, ni formularios, ni retenciones. Tras cancelar mantienes acceso
          hasta el final del período que ya has pagado.
        </P>

        <H2>Pro Mensual</H2>
        <ul className="list-none p-0 m-0 pl-4">
          <Li>
            <strong className="text-ds-text">Primeros 14 días tras tu primer upgrade:</strong>{' '}
            reembolso del 100%, sin preguntas. Tu balance de créditos vuelve al estado previo al
            upgrade.
          </Li>
          <Li>
            <strong className="text-ds-text">Después de 14 días:</strong> no aplicamos prorrata del
            mes en curso — el servicio ya se ha prestado. Puedes cancelar y seguir usándolo hasta
            fin de período.
          </Li>
        </ul>

        <H2>Pro Anual</H2>
        <ul className="list-none p-0 m-0 pl-4">
          <Li>
            <strong className="text-ds-text">Primeros 30 días:</strong> reembolso del 100%.
          </Li>
          <Li>
            <strong className="text-ds-text">Después de 30 días:</strong> reembolso prorrateado de
            los <em>meses completos no consumidos</em>, siempre que hayas utilizado{' '}
            <strong className="text-ds-text">menos del 20%</strong> de los créditos del período
            actual. Si has exprimido el plan, no hay reembolso del dinero ya convertido en servicio.
          </Li>
        </ul>

        <H2>Packs de créditos (20 / 60 / 150)</H2>
        <ul className="list-none p-0 m-0 pl-4">
          <Li>
            <strong className="text-ds-text">Menos del 20% consumido + dentro de 14 días:</strong>{' '}
            reembolso del 100%.
          </Li>
          <Li>
            <strong className="text-ds-text">Entre 20% y 80% consumido + dentro de 14 días:</strong>{' '}
            reembolso prorrateado del saldo no consumido.
          </Li>
          <Li>
            <strong className="text-ds-text">Más del 80% consumido o pasados 14 días:</strong> no
            aplica reembolso (producto consumible prestado).
          </Li>
        </ul>

        <H2>Garantías técnicas</H2>
        <P>
          No son <em>policy</em> — son cómo funciona el producto por diseño:
        </P>
        <ul className="list-none p-0 m-0 pl-4">
          <Li>
            <strong className="text-ds-text">Si falla la generación del modelo de IA,</strong> el
            crédito se devuelve automáticamente a tu balance. No pagas por cosas que no funcionaron.
          </Li>
          <Li>
            <strong className="text-ds-text">Si usas tu propia API key (BYOK),</strong> una
            generación fallida no afecta tu balance de créditos — porque BYOK no los consume.
          </Li>
        </ul>

        <H2>Protección contra abusos</H2>
        <P>
          Aplicamos{' '}
          <strong className="text-ds-text">un reembolso por usuario y tipo de plan</strong>{' '}
          (Monthly, Anual, cada pack). Si te reembolsamos un Pro Mensual, no puedes re-suscribirte
          al mismo plan para volver a pedirlo.
        </P>
        <P>
          Si abres una disputa con tu banco (<em>chargeback</em>) sin haber solicitado primero un
          reembolso por nuestros canales, la cuenta queda suspendida y no podrás volver a
          suscribirte con esa tarjeta o correo.
        </P>
        <P>
          Los patrones de <em>refund abuse</em> detectados automáticamente — misma tarjeta o IP o
          dispositivo con múltiples cuentas pidiendo reembolso — también resultan en bloqueo.
        </P>

        <H2>Cómo pedirlo</H2>
        <P>
          Escribe a{' '}
          <a
            href="mailto:refunds@guioncreator.com"
            className="text-ds-red no-underline hover:opacity-80"
          >
            refunds@guioncreator.com
          </a>{' '}
          desde el correo de tu cuenta con el motivo y el cargo concreto. Respondemos en menos de{' '}
          <strong className="text-ds-text">48 h laborables</strong>.
        </P>

        <div className="mt-14 pt-8 border-t border-ds-border">
          <p className="font-sans italic text-[13px] text-ds-muted leading-[1.6]">
            Esta política puede revisarse. Cualquier cambio afecta solo a suscripciones o packs
            comprados después del cambio.
          </p>
        </div>

        <div className="mt-10 flex items-center gap-4 flex-wrap">
          <Link
            to="/"
            className="font-display font-bold text-[12px] tracking-btn uppercase text-ds-muted no-underline hover:text-ds-text transition-colors"
          >
            ← Volver a la portada
          </Link>
          <span className="text-ds-border-mid">·</span>
          <Link
            to="/pricing"
            className="font-display font-bold text-[12px] tracking-btn uppercase text-ds-muted no-underline hover:text-ds-text transition-colors"
          >
            Ver precios
          </Link>
          <span className="text-ds-border-mid">·</span>
          <a
            href="https://status.thallein.com/status/guioncreator"
            target="_blank"
            rel="noopener noreferrer"
            className="font-display font-bold text-[12px] tracking-btn uppercase text-ds-muted no-underline hover:text-ds-text transition-colors"
          >
            Status público ↗
          </a>
        </div>
      </article>
    </div>
  )
}
